import type { TaskPriority } from '../../../shared/lib/datetime';
import { todayIso, tomorrowIso, formatTaskDateLabel } from '../../../shared/lib/datetime';

export interface QuickAddParseContext {
  today: string;
  tomorrow: string;
  defaultDueDate: string | null;
  timezone: string;
}

export interface QuickAddParseResult {
  title: string;
  dueDate: string | null;
  dueTime: string | null;
  priority: TaskPriority | null;
  tagNames: string[];
  hasExplicitDate: boolean;
  hasExplicitTime: boolean;
  hasPriorityToken: boolean;
}

const TIME_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;
const DMY_RE = /^(\d{1,2})\.(\d{1,2})(?:\.(\d{2,4}))?$/;

const PRIORITY_ALIASES: Record<string, TaskPriority> = {
  urgent: 'urgent',
  very: 'urgent',
  'очень-срочно': 'urgent',
  'очень-срочный': 'urgent',
  критично: 'urgent',
  критический: 'urgent',
  high: 'high',
  h: 'high',
  высокий: 'high',
  высокая: 'high',
  срочно: 'high',
  medium: 'medium',
  m: 'medium',
  средний: 'medium',
  средняя: 'medium',
  normal: 'medium',
  нормальный: 'medium',
  low: 'low',
  l: 'low',
  низкий: 'low',
  низкая: 'low',
};

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function parseClockToken(token: string): string | null {
  const match = TIME_RE.exec(token);
  if (match === null) {
    return null;
  }
  const hourText = match[1];
  const minuteText = match[2];
  if (hourText === undefined || minuteText === undefined) {
    return null;
  }
  const hour = Number(hourText);
  if (!Number.isInteger(hour) || hour < 0 || hour > 23) {
    return null;
  }
  return `${pad2(hour)}:${minuteText}`;
}

function parseDateToken(token: string, today: string): string | null {
  const match = DMY_RE.exec(token);
  if (match === null) {
    return null;
  }
  const dayText = match[1];
  const monthText = match[2];
  const yearText = match[3];
  if (dayText === undefined || monthText === undefined) {
    return null;
  }
  const day = Number(dayText);
  const month = Number(monthText);
  let year: number;
  if (yearText === undefined) {
    year = Number(today.slice(0, 4));
  } else if (yearText.length === 2) {
    year = 2000 + Number(yearText);
  } else {
    year = Number(yearText);
  }
  if (
    !Number.isInteger(day) ||
    !Number.isInteger(month) ||
    !Number.isInteger(year) ||
    day < 1 ||
    day > 31 ||
    month < 1 ||
    month > 12 ||
    year < 2000 ||
    year > 2100
  ) {
    return null;
  }
  return `${pad2(year)}-${pad2(month)}-${pad2(day)}`;
}

/**
 * Умный разбор быстрого добавления:
 * `завтра 18:00 Купить молоко #дом !высокий`.
 */
export function parseQuickAdd(raw: string, ctx: QuickAddParseContext): QuickAddParseResult {
  const tokens = raw.trim().split(/\s+/u).filter((token) => token.length > 0);
  const titleParts: string[] = [];
  const tagNames: string[] = [];
  let dueDate: string | null = null;
  let hasExplicitDate = false;
  let dueTime: string | null = null;
  let hasExplicitTime = false;
  let priority: TaskPriority | null = null;
  let hasPriorityToken = false;

  for (const token of tokens) {
    if (token.startsWith('#') && token.length > 1) {
      const name = token.slice(1);
      const exists = tagNames.some((tag) => tag.toLowerCase() === name.toLowerCase());
      if (!exists) {
        tagNames.push(name);
      }
      continue;
    }

    if (token.startsWith('!') && token.length > 1) {
      const key = token.slice(1).toLowerCase();
      const mapped = PRIORITY_ALIASES[key];
      if (mapped !== undefined) {
        priority = mapped;
        hasPriorityToken = true;
        continue;
      }
      titleParts.push(token);
      continue;
    }

    const lower = token.toLowerCase();
    if (lower === 'сегодня' || lower === 'сёдня') {
      dueDate = ctx.today;
      hasExplicitDate = true;
      continue;
    }
    if (lower === 'завтра') {
      dueDate = ctx.tomorrow;
      hasExplicitDate = true;
      continue;
    }

    const time = parseClockToken(token);
    if (time !== null) {
      dueTime = time;
      hasExplicitTime = true;
      continue;
    }

    const date = parseDateToken(token, ctx.today);
    if (date !== null) {
      dueDate = date;
      hasExplicitDate = true;
      continue;
    }

    titleParts.push(token);
  }

  const title = titleParts.join(' ').trim();
  const finalDate = hasExplicitDate ? dueDate : ctx.defaultDueDate;

  return {
    title,
    dueDate: finalDate,
    dueTime,
    priority,
    tagNames,
    hasExplicitDate,
    hasExplicitTime,
    hasPriorityToken,
  };
}

export function formatQuickDateLabel(dateIso: string | null, timezone: string): string {
  if (dateIso === null) {
    return 'Без даты';
  }
  const today = todayIso(timezone);
  const tomorrow = tomorrowIso(timezone);
  if (dateIso === today) {
    return 'Сегодня';
  }
  if (dateIso === tomorrow) {
    return 'Завтра';
  }
  return formatTaskDateLabel(dateIso, timezone);
}

export function describeRecurrenceSummary(text: string | null): string | null {
  if (text === null || text.trim().length === 0) {
    return null;
  }
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return null;
  }
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }
  const obj = raw as Record<string, unknown>;
  return describeRecurrenceRule({
    frequency: obj['frequency'],
    interval: obj['interval'],
    weekdays: obj['weekdays'],
    end_date: obj['end_date'],
  });
}

/**
 * Понятное превью правила повторения:
 * «каждую среду», «через 3 дня», «каждые 2 недели по пн, ср», «ежемесячно».
 */
export function describeRecurrenceRule(obj: {
  frequency?: unknown;
  interval?: unknown;
  weekdays?: unknown;
  end_date?: unknown;
}): string | null {
  const frequency = obj.frequency;
  const intervalRaw = obj.interval;
  const interval =
    typeof intervalRaw === 'number' && Number.isFinite(intervalRaw)
      ? Math.trunc(intervalRaw)
      : 1;
  const safeInterval = interval < 1 ? 1 : interval;
  const weekdaysRaw = obj.weekdays;
  const weekdays = Array.isArray(weekdaysRaw)
    ? weekdaysRaw.filter((value): value is number => typeof value === 'number' && value >= 0 && value <= 6)
    : [];
  const labels = weekdays.map((day) => WEEKDAY_SHORT[day] ?? '').filter((label) => label.length > 0);
  const endRaw = obj.end_date;
  const endNote =
    typeof endRaw === 'string' && endRaw.length > 0 ? ` · до ${endRaw}` : '';

  if (frequency === 'daily') {
    if (safeInterval === 1) {
      return `Ежедневно${endNote}`;
    }
    if (safeInterval === 2) {
      return `Через день${endNote}`;
    }
    return `Каждые ${safeInterval} дн.${endNote}`;
  }
  if (frequency === 'weekly') {
    if (safeInterval === 1) {
      if (labels.length === 1) {
        const day = weekdays[0];
        const full = day !== undefined ? (WEEKDAY_FULL[day] ?? '') : '';
        if (full.length > 0) {
          return `Каждую ${inflectWeekday(full)}${endNote}`;
        }
      }
      if (labels.length > 1) {
        return `Еженедельно: ${labels.join(', ')}${endNote}`;
      }
      return `Еженедельно${endNote}`;
    }
    const base = `Каждые ${safeInterval} нед.`;
    return labels.length > 0 ? `${base}: ${labels.join(', ')}${endNote}` : `${base}${endNote}`;
  }
  if (frequency === 'monthly') {
    if (safeInterval === 1) {
      return `Ежемесячно${endNote}`;
    }
    return `Каждые ${safeInterval} мес.${endNote}`;
  }
  return null;
}

/** «среда» → «среду»; «понедельник» → «понедельник»; «воскресенье» → «воскресенье». */
function inflectWeekday(full: string): string {
  const table: Record<string, string> = {
    понедельник: 'понедельник',
    вторник: 'вторник',
    среда: 'среду',
    четверг: 'четверг',
    пятница: 'пятницу',
    суббота: 'субботу',
    воскресенье: 'воскресенье',
  };
  return table[full] ?? full;
}

export const WEEKDAY_SHORT = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'] as const;
export const WEEKDAY_FULL = [
  'понедельник',
  'вторник',
  'среда',
  'четверг',
  'пятница',
  'суббота',
  'воскресенье',
] as const;

export function formatDurationLabel(minutes: number | null): string | null {
  if (minutes === null || !Number.isFinite(minutes) || minutes <= 0) {
    return null;
  }
  const total = Math.trunc(minutes);
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours > 0 && rest > 0) {
    return `${hours} ч ${rest} мин`;
  }
  if (hours > 0) {
    return `${hours} ч`;
  }
  return `${rest} мин`;
}
